// Member-name fields: the text an author types, and the names the upstream output sample can suggest.
import type { BinaryReference } from '@/types'
import type { ScopeNode } from '@/types/bbel-types'
import { sampleFromHint } from '@/views/builder/components/bbel/bbel-palette'

const trimmed = (name: string) => name.trim()
const notEmpty = (name: string) => name !== ''

/** A member chain as typed: "manager.employeeNumber" is ["manager", "employeeNumber"]. */
export function chainFromText(text: string): string[] {
  return text.split('.').map(trimmed).filter(notEmpty)
}

export function textFromChain(chain: string[]): string {
  return chain.join('.')
}

/** Member names as typed: "method, amount" is ["method", "amount"]. */
export function namesFromText(text: string): string[] {
  return text.split(',').map(trimmed).filter(notEmpty)
}

export function textFromNames(names: string[]): string {
  return names.join(', ')
}

export interface MemberSuggestion {
  name: string
  /** The value this member had in the sample, such as `"P-1"`. */
  sample: string
}

export interface MemberSuggestions {
  members: MemberSuggestion[]
  /** True when the data is an array of plain values, so an element has no field to name. */
  plain: boolean
}

/**
 * Which members a field offers.
 * - `chains`: every value that is not an array. The members of a nested object are offered as dotted chains.
 * - `names`: the element's own members that are not arrays.
 * - `arrays`: the arrays. One inside a nested object is offered as a dotted chain.
 */
export type MemberKind = 'chains' | 'names' | 'arrays'

// The sample draws an array as a node with one child, "[0]", that holds the shape of its elements
// (bbel-scope-output.ts). A node without that child is an object or a plain value.
function sampledElement(node: ScopeNode): ScopeNode | undefined {
  return node.children?.find((child) => child.segment === '[0]')
}

// An array with no sampled element has no "[0]" child, so its hint is what says it is an array. The hint
// text comes from valueHint in bbel-scope-output.ts.
function isArray(node: ScopeNode): boolean {
  return sampledElement(node) !== undefined || (node.hint?.startsWith('array') ?? false)
}

/** Walks a member chain down from a node. An array on the way is entered through its sampled element. */
export function memberNode(node: ScopeNode | undefined, chain: string[]): ScopeNode | undefined {
  let current = node
  for (const name of chain) {
    if (!current) return undefined
    const holder: ScopeNode = sampledElement(current) ?? current
    current = holder.children?.find((child) => child.segment === name)
  }
  return current
}

/** The scope node a reference points at, or undefined when no sample reaches it. */
export function referencedNode(roots: ScopeNode[], reference: BinaryReference): ScopeNode | undefined {
  const services = roots.find((root) => root.segment === 'services')
  const service = services?.children?.find((child) => child.segment === reference.serviceId)
  const projectionSegment = reference.projection.toLowerCase()
  const projection = service?.children?.find((child) => child.segment === projectionSegment)
  return memberNode(projection, reference.path)
}

/**
 * Where each level of a Flatten starts: the source position for the first level, then the array each level
 * names. The result has one more entry than there are levels; entry n + 1 is the array level n walks.
 */
export function levelPositions(source: ScopeNode | undefined, levels: { path: string[] }[]): (ScopeNode | undefined)[] {
  const positions = [source]
  for (const level of levels) {
    const above = positions[positions.length - 1]
    positions.push(memberNode(above, level.path))
  }
  return positions
}

/** The members a field can suggest for the data at a node. An array is read through its sampled element. */
export function memberSuggestions(node: ScopeNode | undefined, kind: MemberKind): MemberSuggestions {
  if (!node) return { members: [], plain: false }

  const element = sampledElement(node)
  const elementIsPlain = element !== undefined && (element.children?.length ?? 0) === 0
  if (elementIsPlain) return { members: [], plain: true }

  return { members: membersOf(element ?? node, '', kind), plain: false }
}

function membersOf(parent: ScopeNode, prefix: string, kind: MemberKind): MemberSuggestion[] {
  const members: MemberSuggestion[] = []
  for (const child of parent.children ?? []) {
    const name = prefix + child.segment
    const childIsArray = isArray(child)
    const isObject = !childIsArray && (child.children?.length ?? 0) > 0

    const offered = kind === 'arrays' ? childIsArray : !childIsArray && !(kind === 'chains' && isObject)
    if (offered) members.push({ name, sample: sampleFromHint(child.hint) })

    const walksInto = isObject && kind !== 'names'
    if (walksInto) members.push(...membersOf(child, `${name}.`, kind))
  }
  return members
}
